import { useState } from 'react';
import { bangLevels } from '../../simulation/controller';
import { useApp } from '../../state/store';
import { BASE_CONTROLLER, SPRING_PLANT } from '../../content/presets';
import { MathTex } from '../MathTex';
import { NumberField } from '../sim/NumberField';
import { parseMarkup } from '../Markup';

// ── Notation primer ──────────────────────────────────────────────────
const NOTATION: { tex: string; name: string; means: string; example: string }[] = [
  { tex: 'm\\,g \\;\\text{ or }\\; m \\cdot g', name: 'Side by side, or a dot', means: 'Multiply.', example: '1 kg × 9.81 m/s² = 9.81 N' },
  { tex: 'k\\,(z - z_s)', name: 'Parentheses', means: 'Work out the inside first, then multiply.', example: '2 × (2.5 − 2) = 1 N' },
  { tex: '\\frac{b}{m}', name: 'Fraction bar', means: 'Divide the top by the bottom.', example: 'b/m = 1/1 = 1 per second' },
  { tex: 'K_p,\\ z_0', name: 'Subscript', means: 'A label saying which one. Not maths.', example: 'K p is the proportional gain' },
  { tex: 's^2', name: 'Superscript', means: 'A power: s² = s × s.', example: 'm/s² = metres per second, per second' },
  { tex: '[q,\\ v]^T', name: 'Superscript T', means: 'Exception: write this list as a column (transpose). Not a power, not thrust.', example: 'q stacked above v' },
  { tex: '\\dot z', name: 'Dot above', means: 'Rate of change with time.', example: '$\\dot z = v$' },
  { tex: '\\Delta t', name: 'Δ (Greek delta)', means: 'A change in, or a small step of.', example: 'Δt = 0.005 s' },
  { tex: '\\zeta,\\ \\omega_n,\\ \\tau,\\ \\alpha', name: 'Greek letters', means: 'Names: zeta, omega, tau, alpha.', example: 'ζ is the damping ratio' },
  { tex: '\\sqrt{x}', name: 'Square root', means: 'The number n where n × n = x.', example: '√16 = 4' },
  { tex: '\\operatorname{clamp}(x, 0, 25)', name: 'Function name', means: 'Apply a named rule to the values in brackets.', example: 'clamp(30, 0, 25) = 25' },
  { tex: 'm = 1', name: 'Scalar', means: 'One number (with units).', example: 'mass, time, one gain' },
  { tex: 'x = \\begin{bmatrix} q \\\\ v\\end{bmatrix}', name: 'Vector', means: 'An ordered list of numbers. Here written as a column.', example: '[0.3, −0.1]ᵀ' },
  { tex: 'A = \\begin{bmatrix} 0 & 1 \\\\ -2 & -1\\end{bmatrix}', name: 'Matrix', means: 'A table of numbers in rows and columns (here 2×2).', example: '2×2 means 2 rows, 2 columns' },
];

export function NotationPrimer() {
  return (
    <div className="table-scroll">
      <table className="content-table notation">
        <thead>
          <tr>
            <th scope="col">Looks like</th>
            <th scope="col">Name</th>
            <th scope="col">Means</th>
            <th scope="col">Example</th>
          </tr>
        </thead>
        <tbody>
          {NOTATION.map((r) => (
            <tr key={r.name}>
              <td>
                <MathTex tex={r.tex} />
              </td>
              <th scope="row">{r.name}</th>
              <td>{r.means}</td>
              <td>{parseMarkup(r.example, r.name)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Block diagram ────────────────────────────────────────────────────
export function BlockDiagram() {
  const highlight = useApp((s) => s.highlight);
  const highlightSticky = useApp((s) => s.highlightSticky);
  const setHighlight = useApp((s) => s.setHighlight);

  const flash = (key: 'signal:Tff' | 'signal:e') => {
    if (highlight === key && highlightSticky) {
      setHighlight(null);
      return;
    }
    setHighlight(key, true);
    requestAnimationFrame(() => {
      document.querySelector(`[data-hl="${key}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  };

  const hlFf = highlight === 'signal:Tff';
  const hlE = highlight === 'signal:e';

  return (
    <figure className="widget diagram">
      <svg
        viewBox="0 0 640 250"
        role="img"
        aria-labelledby="bd-title bd-desc"
        className={`block-diagram${hlFf ? ' hl-ff' : ''}${hlE ? ' hl-e' : ''}`}
      >
        <title id="bd-title">Altitude control block diagram</title>
        <desc id="bd-desc">See the text description below the diagram.</desc>
        <defs>
          <marker id="bd-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 z" fill="context-stroke" />
          </marker>
        </defs>
        {/* feedforward path */}
        <g className="ff-path">
          <rect x="200" y="12" width="120" height="36" rx="6" />
          <text x="260" y="34" textAnchor="middle">Model: T_ff</text>
          <text x="330" y="26" className="path-label">feedforward path</text>
          <path d="M320,30 H420 V96" markerEnd="url(#bd-arrow)" />
        </g>
        {/* reference to summing junction */}
        <text x="8" y="116">target r</text>
        <path d="M60,112 H92" markerEnd="url(#bd-arrow)" className="wire" />
        <circle cx="104" cy="112" r="12" className="sum" />
        <text x="104" y="116" textAnchor="middle">Σ</text>
        <text x="84" y="104" className="sign">+</text>
        <text x="108" y="138" className="sign">−</text>
        <path d="M116,112 H150" markerEnd="url(#bd-arrow)" className="wire" />
        <text x="124" y="104" className={`path-label${hlE ? ' is-hl' : ''}`}>
          e
        </text>
        {/* feedback controller */}
        <g className="fb-path">
          <rect x="150" y="94" width="120" height="36" rx="6" />
          <text x="210" y="116" textAnchor="middle">Controller</text>
          <path d="M270,112 H408" markerEnd="url(#bd-arrow)" />
          <text x="300" y="104" className="path-label">u_fb</text>
        </g>
        <circle cx="420" cy="112" r="12" className="sum" />
        <text x="420" y="116" textAnchor="middle">Σ</text>
        <path d="M432,112 H462" markerEnd="url(#bd-arrow)" className="wire" />
        <text x="436" y="104" className="path-label">T_cmd</text>
        <rect x="462" y="94" width="70" height="36" rx="6" className="block" />
        <text x="497" y="110" textAnchor="middle">Rotors</text>
        <text x="497" y="123" textAnchor="middle" className="small-text">clamp</text>
        <path d="M532,112 H560" markerEnd="url(#bd-arrow)" className="wire" />
        <text x="536" y="104" className="path-label">T</text>
        {/* plant with disturbance */}
        <rect x="560" y="94" width="72" height="36" rx="6" className="block" />
        <text x="596" y="116" textAnchor="middle">Vehicle</text>
        <text x="560" y="70" className="dist-label">load d</text>
        <path d="M596,74 V94" markerEnd="url(#bd-arrow)" className="wire dist" />
        {/* sensor feedback */}
        <path d="M596,130 V200 H470" markerEnd="url(#bd-arrow)" className="wire" />
        <text x="602" y="170" className="path-label">z</text>
        <rect x="370" y="182" width="100" height="36" rx="6" className="block" />
        <text x="420" y="204" textAnchor="middle">Altimeter</text>
        <text x="440" y="236" className="dist-label">noise n</text>
        <g className="fb-path">
          <path d="M370,200 H104 V124" markerEnd="url(#bd-arrow)" />
          <text x="200" y="194" className="path-label">measured y_m (feedback path)</text>
        </g>
      </svg>
      <figcaption>
        <div className="row gap-s wrap">
          <button type="button" className={`btn small ghost${hlFf ? ' on' : ''}`} aria-pressed={hlFf} onClick={() => flash('signal:Tff')}>
            Highlight feedforward in plots
          </button>
          <button type="button" className={`btn small ghost${hlE ? ' on' : ''}`} aria-pressed={hlE} onClick={() => flash('signal:e')}>
            Highlight error in plots
          </button>
        </div>
        <details>
          <summary>Text description of the diagram</summary>
          <ol className="small">
            <li>The model predicts a thrust T_ff and sends it straight to a summing point before the rotors (feedforward path).</li>
            <li>The target r and the measured altitude y_m meet at a summing point that forms the error e = r − y_m.</li>
            <li>The controller turns e into a correction u_fb (feedback path).</li>
            <li>T_cmd = T_ff + u_fb goes to the rotors, which clamp it to 0…T_max and apply thrust T.</li>
            <li>The vehicle responds to T and to any outside load d, producing altitude z.</li>
            <li>The altimeter measures z (plus noise n) and reports y_m, closing the loop.</li>
          </ol>
        </details>
      </figcaption>
    </figure>
  );
}

// ── Integral as area ─────────────────────────────────────────────────
export function IntegralArea() {
  const [e1, setE1] = useState(0.05);
  const [d1, setD1] = useState(10);
  const [e2, setE2] = useState(0.4);
  const [d2, setD2] = useState(0.5);
  const a1 = e1 * d1;
  const a2 = e2 * d2;
  const W = 300;
  const H = 90;
  const tMax = Math.max(d1, d2, 1);
  const eMax = Math.max(e1, e2, 0.05);
  const bar = (e: number, dur: number, cls: string) => (
    <rect x={0} y={H - (e / eMax) * (H - 10)} width={(dur / tMax) * W} height={(e / eMax) * (H - 10)} className={cls} />
  );
  return (
    <div className="widget" role="group" aria-label="Accumulated error comparison">
      <p className="widget-title">Which piles up more: small and long, or big and brief?</p>
      <div className="grid-2">
        <div>
          <p className="small strong">A: small, long error</p>
          <NumberField name="Error A" label="error" unit="m" value={e1} min={0} max={0.5} step={0.01} onCommit={setE1} onLive={setE1} />
          <NumberField name="Duration A" label="lasting" unit="s" value={d1} min={0} max={20} step={0.5} onCommit={setD1} onLive={setD1} />
        </div>
        <div>
          <p className="small strong">B: big, brief error</p>
          <NumberField name="Error B" label="error" unit="m" value={e2} min={0} max={0.5} step={0.01} onCommit={setE2} onLive={setE2} />
          <NumberField name="Duration B" label="lasting" unit="s" value={d2} min={0} max={20} step={0.5} onCommit={setD2} onLive={setD2} />
        </div>
      </div>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Area A ${a1.toFixed(2)} m·s, area B ${a2.toFixed(2)} m·s.`} className="mini-plot">
        {bar(e1, d1, 'area-a')}
        {bar(e2, d2, 'area-b')}
      </svg>
      <p className="calc" aria-live="polite">
        A: {e1} m × {d1} s = <strong>{a1.toFixed(2)} m·s</strong> · B: {e2} m × {d2} s = <strong>{a2.toFixed(2)} m·s</strong> →{' '}
        {Math.abs(a1 - a2) < 1e-9 ? 'equal' : a1 > a2 ? 'A accumulates more' : 'B accumulates more'}
      </p>
    </div>
  );
}

// ── Bang-bang levels ─────────────────────────────────────────────────
export function BangLevels() {
  const exp = useApp((s) => s.experiment);
  const cfg = exp?.config.controller.kind === 'bang-bang' ? exp.config : null;
  const [delta, setDelta] = useState(cfg?.controller.bangDelta ?? 3);
  const plant = cfg?.plant ?? SPRING_PLANT;
  const lv = bangLevels({ ...(cfg?.controller ?? BASE_CONTROLLER), bangDelta: delta }, plant, cfg?.z0 ?? 2, cfg?.reference.initial ?? 2);
  return (
    <div className="widget" role="group" aria-label="Bang-bang levels">
      <p className="widget-title">The two thrust levels, after rotor limits</p>
      <NumberField name="Amplitude Delta" label="Δ" unit="N" value={delta} min={0} max={15} step={0.5} onCommit={setDelta} onLive={setDelta} />
      <table className="kv">
        <tbody>
          <tr>
            <th scope="row">High level requested</th>
            <td>
              T₀ + Δ = {lv.highRequested.toFixed(2)} N → applied <strong>{lv.high.toFixed(2)} N</strong>
            </td>
          </tr>
          <tr>
            <th scope="row">Low level requested</th>
            <td>
              T₀ − Δ = {lv.lowRequested.toFixed(2)} N → applied <strong>{lv.low.toFixed(2)} N</strong>
            </td>
          </tr>
        </tbody>
      </table>
      {(lv.lowRequested < 0 || lv.highRequested > plant.Tmax) && (
        <p className="small warn-text">A requested level is outside 0…{plant.Tmax} N. Rotors can't pull down or exceed T_max, so the applied level is clamped. The levels are no longer symmetric.</p>
      )}
    </div>
  );
}

// ── Metric definitions ───────────────────────────────────────────────
export function MetricDefinitions() {
  const W = 320;
  const H = 150;
  const pts: string[] = [];
  const y = (z: number) => H - 20 - ((z - 2) / 0.7) * (H - 40);
  const x = (t: number) => 30 + (t / 8) * (W - 40);
  for (let t = 0; t <= 8; t += 0.05) {
    const z = t < 1 ? 2 : 2.5 - 0.5 * Math.exp(-1.6 * (t - 1)) * (Math.cos(3 * (t - 1)) + 0.53 * Math.sin(3 * (t - 1)));
    pts.push(`${pts.length ? 'L' : 'M'}${x(t).toFixed(1)},${y(z).toFixed(1)}`);
  }
  return (
    <figure className="widget">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Example step response from 2 m to 2.5 m at 1 s, peaking near 2.6 m, then settling inside a ±0.05 m band." className="mini-plot">
        <rect x={x(0)} y={y(2.55)} width={x(8) - x(0)} height={y(2.45) - y(2.55)} className="band" />
        <line x1={x(0)} x2={x(8)} y1={y(2.5)} y2={y(2.5)} className="ref-line" />
        <path d={pts.join('')} fill="none" stroke="var(--c-z)" strokeWidth={2} />
        <text x={x(8)} y={y(2.55) - 3} textAnchor="end" className="tick">
          ±0.05 m band
        </text>
        <text x={x(1.6)} y={y(2.66)} className="tick">
          overshoot
        </text>
        <text x={x(0)} y={y(2) - 4} className="tick">
          2 m
        </text>
      </svg>
      <dl className="defs small">
        <dt>Rise time</dt>
        <dd>Time from 10 % to 90 % of the way to the new target (2.05 m → 2.45 m for this step).</dd>
        <dt>Overshoot</dt>
        <dd>Peak beyond the target ÷ step size. Peak 2.6 m on a 0.5 m step → 20 %.</dd>
        <dt>Settling time</dt>
        <dd>From the event until the altitude enters the ±0.05 m band and stays there for the rest of the run. Reports not settled in this run if it never does.</dd>
        <dt>Residual error</dt>
        <dd>Mean error over the final 2 s of the run.</dd>
        <dt>Control effort and saturation</dt>
        <dd>How hard the rotors work: watch the requested thrust; shaded time = requesting beyond 0 … T_max.</dd>
        <dt>Accumulated error magnitude</dt>
        <dd>Area under |error| over the run (m·s): big or long-lasting errors both count.</dd>
      </dl>
    </figure>
  );
}
